import React, { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useState } from 'react';

import { classnames } from '@/utils/classnames.ts';

import Tag from './Tag.tsx';

interface DateColumnEntry {
    text: string;
    // A red tag rather than text, which takes more room.
    isOverdue: boolean;
}

interface DateColumnContextValue {
    // The dates that may be the widest, which every cell holds invisibly to take their width.
    widest: DateColumnEntry[];
    register: (id: string, entry: DateColumnEntry | null) => void;
}

const DateColumnContext = createContext<DateColumnContextValue | null>(null);

// A tag's padding is about as wide as this many digits.
const TAG_EXTRA_CHARACTERS = 2;
// A date this many characters shorter than the longest can still be as wide, e.g. "Mär" next to "Okt".
const WIDTH_TOLERANCE = 1;

const sizeOf = ({ text, isOverdue }: DateColumnEntry) => text.length + (isOverdue ? TAG_EXTRA_CHARACTERS : 0);

// The dates of the rows inside it in one column as wide as the widest of them, across all of its lists, so what's in
// front of the dates lines up, also between a page's lists by day. Each DateColumnCell says its date, and every cell
// holds the widest ones invisibly; CSS alone can't size a column of flex rows by its widest cell.
export const DateColumnProvider = ({ children }: { children: React.ReactNode }) => {
    const [entries, setEntries] = useState<Map<string, DateColumnEntry>>(new Map());

    // The same function throughout, so a cell's effect only runs again when its own date changes.
    const register = useCallback(
        (id: string, entry: DateColumnEntry | null) =>
            setEntries((current) => {
                const previous = current.get(id);
                if (entry && previous?.text === entry.text && previous.isOverdue === entry.isOverdue) return current;
                if (!entry && !previous) return current;
                const next = new Map(current);
                if (entry) next.set(id, entry);
                else next.delete(id);
                return next;
            }),
        []
    );

    const value = useMemo(() => {
        const all = [...entries.values()];
        const largest = Math.max(0, ...all.map(sizeOf));
        const widest = [
            ...new Map(
                all
                    .filter((entry) => sizeOf(entry) >= largest - WIDTH_TOLERANCE)
                    .map((entry) => [`${entry.isOverdue}:${entry.text}`, entry])
            ).values(),
        ];
        return { widest, register };
    }, [entries, register]);

    return <DateColumnContext.Provider value={value}>{children}</DateColumnContext.Provider>;
};

export interface DateColumnCellProps extends Omit<React.ComponentPropsWithRef<'span'>, 'children'> {
    // As short as it goes, e.g. "10. Okt"; null leaves the cell empty.
    text: string | null;
    // A red tag rather than text, e.g. a to-do past its due date.
    isOverdue?: boolean;
}

// A row's date, right-aligned in a column as wide as the widest date of its DateColumnProvider; outside one, as wide
// as its own.
const DateColumnCell = ({ text, isOverdue = false, className, ...props }: DateColumnCellProps) => {
    const id = useId();
    const context = useContext(DateColumnContext);
    const register = context?.register;

    useLayoutEffect(() => {
        if (!register) return;
        register(id, text ? { text, isOverdue } : null);
        return () => register(id, null);
    }, [register, id, text, isOverdue]);

    const render = (entry: DateColumnEntry) => (entry.isOverdue ? <Tag tone="negative">{entry.text}</Tag> : entry.text);

    return (
        <span
            {...props}
            className={classnames(
                'grid min-w-14 shrink-0 justify-items-end text-sm whitespace-nowrap text-text-muted tabular-nums',
                className
            )}
        >
            {context?.widest.map((entry) => (
                <span key={`${entry.isOverdue}:${entry.text}`} aria-hidden className="invisible [grid-area:1/1]">
                    {render(entry)}
                </span>
            ))}
            <span className="[grid-area:1/1]">{text && render({ text, isOverdue })}</span>
        </span>
    );
};

export default DateColumnCell;
